/**
 * Landing Reference Profile — configuration.
 *
 * All numeric assumptions that turn a user's monthly rent into a plausible
 * financial profile live here. Nothing in this file computes anything; it
 * only stores knobs. The inference function (rentProfile.js) reads these
 * values and produces the profile that feeds the existing Core engines.
 *
 * Why a config module rather than inline constants?
 *   1. Product can tune the landing feel without touching business logic.
 *   2. QA can review the assumptions in one place.
 *   3. If we ever localise the app (different city guidelines, e.g. Mumbai
 *      vs Bengaluru salary-to-rent ratios), we swap this file.
 *
 * The rationale for each value is documented in
 *   /app/frontend/src/homeiq/landing/landing-profile.md
 * — please keep the doc in sync when you edit these constants.
 */

export const LANDING_PROFILE_CONFIG = {
    // ── Rent → derived-profile multipliers ─────────────────────────
    // Rent is treated as roughly 20% of monthly take-home for a typical
    // Bengaluru salaried buyer, so salary ≈ rent × 5.
    rentToSalaryMultiplier: 5,

    // Assumed accumulated down-payment corpus ≈ 5 years of rent-equivalent
    // savings capacity. Conservative default that keeps ratios sensible.
    rentToDownPaymentMultiplier: 60,

    // ── Fixed Core-engine inputs (mirror app defaults) ─────────────
    defaultObligations: 0,
    defaultBankRule: 55,     // "Standard" FOIR — matches app default
    defaultTenureYears: 20,  // matches app default
    defaultRatePct: 8.75,    // matches app default
    defaultTargetEMI: 0,     // 0 = "no explicit target" for Core engine
    defaultSegment: "Mid",   // matches app default
    defaultSbaSqft: 1500,    // matches app default

    // ── Guardrails (clamps on inferred outputs) ────────────────────
    // Prevent absurd values at the extremes of the slider (rent = ₹500
    // shouldn't produce a ₹2.5K salary; rent = ₹5 L shouldn't produce a
    // ₹25 L/mo profile). Numbers chosen to stay realistic for Indian
    // salaried buyers.
    clamps: {
        salary:      { min:  40_000, max: 15_00_000 },
        downPayment: { min: 5_00_000, max: 1_00_00_000 },
    },
};
